import { useCallback, useEffect, useRef, useState } from 'react';
import { apiMaybe, ApiError } from './api.js';

interface Versioned {
  v: number;
  serverNow: number;
}

/**
 * The whole real-time story.
 *
 * Poll a versioned endpoint. If the version has not moved the server answers 204
 * and this costs nothing. There is no socket, no room actor, no reconnection
 * token and no resume flow -- reconnecting is just "the next poll succeeds".
 */
export function usePoll<T extends Versioned>(
  path: string,
  token: string | null,
  intervalMs = 2000,
): {
  state: T | null;
  /** A transient blip. The next poll is expected to fix it. */
  error: string | null;
  /**
   * A refusal that polling will never recover from -- the session is gone, or
   * the player has been ejected. Polling stops and the app says so, rather than
   * hammering the server every two seconds with a request it knows will fail.
   */
  fatal: ApiError | null;
  /** serverNow - Date.now(), for rendering countdowns against a phone with a wrong clock. */
  offsetMs: number;
  refresh: () => void;
} {
  const [state, setState] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fatal, setFatal] = useState<ApiError | null>(null);
  const [offsetMs, setOffsetMs] = useState(0);

  // Held in a ref so changing the version never restarts the interval.
  const versionRef = useRef<number | null>(null);
  const inFlight = useRef(false);
  const fatalRef = useRef<ApiError | null>(null);

  const tick = useCallback(
    async (force = false) => {
      if (!token || inFlight.current || fatalRef.current) return;
      inFlight.current = true;
      try {
        const v = force ? null : versionRef.current;
        const next = await apiMaybe<T>(
          v === null ? path : `${path}${path.includes('?') ? '&' : '?'}v=${v}`,
          { token },
        );
        if (next) {
          versionRef.current = next.v;
          setState(next);
          setOffsetMs(next.serverNow - Date.now());
        }
        setError(null);
      } catch (err) {
        // 401 and 403 are answers, not blips: the token is gone or the player has
        // been removed. Retrying cannot help, so stop.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          fatalRef.current = err;
          setFatal(err);
          return;
        }
        // A blip is not an event. The question is already on the phone; say so
        // quietly and let the next poll fix it. The request has its own deadline
        // (REQUEST_TIMEOUT_MS), so a hung connection lands here rather than
        // pinning inFlight and silently ending the night's updates.
        setError(err instanceof Error ? err.message : 'Connection lost');
      } finally {
        inFlight.current = false;
      }
    },
    [path, token],
  );

  useEffect(() => {
    if (!token) return;
    versionRef.current = null;
    fatalRef.current = null;
    setFatal(null);
    void tick(true);
    const id = setInterval(() => void tick(), intervalMs);

    // Coming back from a locked screen should not wait out the interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [tick, token, intervalMs]);

  return { state, error, fatal, offsetMs, refresh: () => void tick(true) };
}

/**
 * Milliseconds left, corrected for the phone's clock being wrong.
 * Display only -- it has no effect on scoring, which the server decides.
 */
export function useCountdown(endsAt: number | null | undefined, offsetMs: number): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (endsAt == null) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [endsAt]);

  if (endsAt == null) return null;
  return Math.max(0, endsAt - (now + offsetMs));
}
